/** The signed-in person's own days from `/api/roster/me`, folded into runs for My schedule. */
import type { OwnSchedule } from '../../pages/roster/MySchedule'
import { addDays, dayRange } from './dates'
import { NOW_FREE, nowOfCode, stackOf } from './derive'

export const OWN_AHEAD_DAYS = 60
const OWN_LOOK_BACK_DAYS = 60

type OwnDay = OwnSchedule['days'][number]

export interface OwnRunCode {
  code: string
  label: string
  colour: string
}

export interface OwnRun {
  from: string
  to: string
  days: number
  value: string
  codes: OwnRunCode[]
  source: 'actual' | 'plan' | 'none'
  projects: string[]
  /** The run's days that carry meals, a lodge night or a comment. */
  notes: OwnDay[]
  /** The run reaches the edge of the window, so its real length is unknown and no sentence may state it. */
  cut: boolean
}

/** Long enough that a current rotation carries its real start date and this month's earlier days are all there. */
export function ownWindow(today: string): { from: string; to: string } {
  return { from: addDays(today, -OWN_LOOK_BACK_DAYS), to: addDays(today, OWN_AHEAD_DAYS) }
}

function hasNotes(day: OwnDay): boolean {
  return Boolean(day.meals || day.lodge || day.comment)
}

/**
 * Actual beats plan, and a change of source breaks a run, as `runsFor` does on the whole document.
 * ⛔ Not `runsFor` itself: it drops a code missing from the legend, and this legend can be empty, so a real shift would read as free.
 */
export function ownRuns(schedule: Pick<OwnSchedule, 'from' | 'to' | 'codes' | 'days'>): OwnRun[] {
  const byDay = new Map(schedule.days.map((d) => [d.day, d] as const))
  const legend = new Map(schedule.codes.map((c) => [c.code, c] as const))
  const runs: OwnRun[] = []
  for (const iso of dayRange(schedule.from, schedule.to)) {
    const day = byDay.get(iso)
    const value = day?.actual || day?.plan || ''
    const source: OwnRun['source'] = day?.actual ? 'actual' : value ? 'plan' : 'none'
    let run = runs.at(-1)
    if (run && run.value === value && run.source === source) {
      run.to = iso
      run.days += 1
    } else {
      run = {
        from: iso,
        to: iso,
        days: 1,
        value,
        source,
        codes: stackOf(value).map((code) => {
          const known = legend.get(code)
          return { code, label: known?.label || code, colour: known?.colour ?? '' }
        }),
        projects: [],
        notes: [],
        cut: false,
      }
      runs.push(run)
    }
    if (day?.project && !run.projects.includes(day.project)) run.projects.push(day.project)
    if (day && hasNotes(day)) run.notes.push(day)
  }
  // No row means nothing published yet: only a gap between known days may read as free.
  while (runs[0]?.source === 'none') runs.shift()
  while (runs.at(-1)?.source === 'none') runs.pop()
  for (const run of runs) run.cut = run.from === schedule.from || run.to === schedule.to
  return runs
}

export interface OwnRunGroups {
  current: OwnRun | undefined
  ahead: OwnRun[]
  earlier: OwnRun[]
}

/** Today's run, what follows it, and the runs that finished earlier this month; anything older is not shown. */
export function groupOwnRuns(runs: OwnRun[], today: string): OwnRunGroups {
  const monthStart = `${today.slice(0, 8)}01`
  return {
    current: runs.find((run) => run.from <= today && today <= run.to),
    ahead: runs.filter((run) => run.from > today),
    earlier: runs.filter((run) => run.to < today && run.to >= monthStart),
  }
}

/** The Right now answer on the desk grid's rule; a code missing from the legend stays visible as itself, never as free. */
export function ownNow(schedule: Pick<OwnSchedule, 'codes' | 'days'>, today: string): string {
  const day = schedule.days.find((d) => d.day === today)
  const leading = stackOf(day?.actual || day?.plan)[0]
  if (!leading) return NOW_FREE
  const code = schedule.codes.find((c) => c.code === leading)
  return code ? nowOfCode(code) : leading
}
